import { useRef, type ComponentProps, type DragEvent } from "react"
import { flexRender } from "@tanstack/react-table"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import { useTableContext } from "./table.context"
import { SPACER_COLUMN_ID } from "./table.constants"
import { reorderColumn } from "./table.state"
import {
  getColumnSizeStyle,
  isInternalColumnId,
  measureNaturalColumnWidth,
} from "./table.utils"

const DRAG_OVER_LEFT = "ui-table-header-cell-drag-over-left"
const DRAG_OVER_RIGHT = "ui-table-header-cell-drag-over-right"

function clearDragClasses(el: HTMLElement) {
  el.classList.remove(DRAG_OVER_LEFT, DRAG_OVER_RIGHT)
}

function handleDragLeave(e: DragEvent<HTMLTableCellElement>) {
  clearDragClasses(e.currentTarget)
}

export type TableHeaderProps = ComponentProps<"thead">

function TableHeader(props: TableHeaderProps) {
  const { className, ...theadProps } = props
  const { table, hasPersistence } = useTableContext()
  const groups = table.getHeaderGroups()
  const { columnSizing, sorting } = table.getState()
  const isMultiSorted = sorting.length > 1
  const dragColumnIdRef = useRef<string | null>(null)
  const isResizingRef = useRef(false)

  function handleDragStart(
    e: DragEvent<HTMLTableCellElement>,
    columnId: string,
  ) {
    dragColumnIdRef.current = columnId
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", columnId)
    requestAnimationFrame(() => {
      ;(e.target as HTMLElement).classList.add(
        "ui-table-header-cell-is-dragging",
      )
    })
  }

  function handleDragOver(e: DragEvent<HTMLTableCellElement>) {
    if (!dragColumnIdRef.current) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    const th = e.currentTarget
    const rect = th.getBoundingClientRect()
    const midpoint = rect.left + rect.width / 2

    if (e.clientX < midpoint) {
      th.classList.add(DRAG_OVER_LEFT)
      th.classList.remove(DRAG_OVER_RIGHT)
    } else {
      th.classList.add(DRAG_OVER_RIGHT)
      th.classList.remove(DRAG_OVER_LEFT)
    }
  }

  function handleDrop(
    e: DragEvent<HTMLTableCellElement>,
    targetColumnId: string,
  ) {
    e.preventDefault()
    clearDragClasses(e.currentTarget)

    const sourceId = dragColumnIdRef.current
    if (!sourceId || sourceId === targetColumnId) return

    const rect = e.currentTarget.getBoundingClientRect()
    const insertAfter = e.clientX >= rect.left + rect.width / 2

    reorderColumn(table, sourceId, targetColumnId, insertAfter)
  }

  function handleDragEnd(e: DragEvent<HTMLTableCellElement>) {
    ;(e.target as HTMLElement).classList.remove(
      "ui-table-header-cell-is-dragging",
    )
    dragColumnIdRef.current = null
    for (const el of document.querySelectorAll(
      `.${DRAG_OVER_LEFT}, .${DRAG_OVER_RIGHT}`,
    )) {
      clearDragClasses(el as HTMLElement)
    }
  }

  return (
    <thead className={cn("ui-table-header", className)} {...theadProps}>
      {groups.map((g) => (
        <tr key={g.id}>
          {g.headers.map((h) => {
            const columnId = h.column.id
            const canSort = h.column.getCanSort()
            const isSorted = h.column.getIsSorted()
            const sortIndex = h.column.getSortIndex()
            const isInternal = isInternalColumnId(columnId)
            const isSpacer = columnId === SPACER_COLUMN_ID
            const canResize = h.column.getCanResize()
            const isResizing = h.column.getIsResizing()
            const canDrag = hasPersistence && !isInternal
            const style = getColumnSizeStyle({
              column: h.column,
              columnSizing,
              hasPersistence,
            })

            if (isSpacer) {
              return <th key={h.id} className="ui-table-spacer-cell" />
            }

            return (
              <th
                key={h.id}
                className={cn("ui-table-header-cell", {
                  "ui-table-header-cell-is-sortable": canSort,
                  "ui-table-header-cell-is-resizing": isResizing,
                })}
                style={style}
                onClick={() => {
                  if (isResizingRef.current) return
                  if (!canSort) return

                  const nextOrder = h.column.getNextSortingOrder()
                  if (nextOrder === false) {
                    h.column.clearSorting()
                  } else {
                    h.column.toggleSorting(nextOrder === "desc", true)
                  }
                }}
                draggable={canDrag}
                onDragStart={
                  canDrag ? (e) => handleDragStart(e, columnId) : undefined
                }
                onDragOver={canDrag ? handleDragOver : undefined}
                onDragLeave={canDrag ? handleDragLeave : undefined}
                onDrop={canDrag ? (e) => handleDrop(e, columnId) : undefined}
                onDragEnd={canDrag ? handleDragEnd : undefined}
              >
                {h.isPlaceholder
                  ? null
                  : flexRender(h.column.columnDef.header, h.getContext())}

                {isSorted && (
                  <>
                    <Icon
                      className="ui-table-sort-icon"
                      src={
                        isSorted === "asc"
                          ? "angle-small-up"
                          : "angle-small-down"
                      }
                    />
                    {isMultiSorted && (
                      <span className="ui-table-sort-priority">
                        {sortIndex + 1}
                      </span>
                    )}
                  </>
                )}

                {canResize && (
                  <div
                    className={cn("ui-table-resize-handle", {
                      "ui-table-resize-handle-is-active": isResizing,
                    })}
                    onMouseDown={(e) => {
                      e.stopPropagation()
                      isResizingRef.current = true

                      const th = e.currentTarget.parentElement as HTMLElement
                      const wasDraggable = th.draggable
                      if (wasDraggable) th.draggable = false

                      const onMouseUp = () => {
                        if (wasDraggable) th.draggable = true
                        setTimeout(() => {
                          isResizingRef.current = false
                        }, 0)
                        document.removeEventListener("mouseup", onMouseUp)
                      }
                      document.addEventListener("mouseup", onMouseUp)

                      h.getResizeHandler()(e)
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation()
                      h.getResizeHandler()(e)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => {
                      e.stopPropagation()
                      const th = e.currentTarget.parentElement as HTMLElement
                      const tableEl = th.closest("table") as HTMLTableElement
                      if (!tableEl) return

                      const parent = th.parentElement
                      if (!parent) return
                      const colIndex = [...parent.children].indexOf(th)
                      const width = measureNaturalColumnWidth(tableEl, colIndex)

                      if (width > 0) {
                        const minSize = h.column.columnDef.minSize ?? 40
                        table.setColumnSizing((prev) => ({
                          ...prev,
                          [columnId]: Math.max(width, minSize) + 1,
                        }))
                      }
                    }}
                  />
                )}
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}

export default TableHeader
